import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BellIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  Cog6ToothIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { notificationService } from "../services/notificationService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

interface NotificationCenterProps {
  // Remove props since we'll fetch data internally
}

const NotificationCenter: React.FC<NotificationCenterProps> = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);

  // Fetch notifications from the server
  const { data: notificationData, isLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => notificationService.getNotifications(50, 0),
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  const notifications = notificationData?.notifications || [];
  const unreadCount = notificationData?.unreadCount || 0;

  // Mutations for notification actions
  const markAsReadMutation = useMutation({
    mutationFn: (notificationId: string) =>
      notificationService.markAsRead(notificationId),
    onMutate: async (notificationId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["notifications"] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(["notifications"]);

      // Optimistically update to mark this notification as read
      queryClient.setQueryData(["notifications"], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          notifications: old.notifications.map((notification: any) =>
            notification.id === notificationId
              ? { ...notification, read: true }
              : notification
          ),
          unreadCount: Math.max(0, old.unreadCount - 1),
        };
      });

      // Return a context object with the snapshotted value
      return { previousData };
    },
    onError: (error, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousData) {
        queryClient.setQueryData(["notifications"], context.previousData);
      }
      // Error handling for mark as read
    },
    onSettled: () => {
      // Always refetch after error or success to ensure server state
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: () => notificationService.markAllAsRead(),
    onMutate: async () => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["notifications"] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(["notifications"]);

      // Optimistically update to mark all as read
      queryClient.setQueryData(["notifications"], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          notifications: old.notifications.map((notification: any) => ({
            ...notification,
            read: true,
          })),
          unreadCount: 0,
        };
      });

      // Return a context object with the snapshotted value
      return { previousData };
    },
    onError: (error, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousData) {
        queryClient.setQueryData(["notifications"], context.previousData);
      }
      // Error handling for mark all as read
    },
    onSettled: () => {
      // Always refetch after error or success to ensure server state
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const deleteNotificationMutation = useMutation({
    mutationFn: (notificationId: string) =>
      notificationService.deleteNotification(notificationId),
    onMutate: async (notificationId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["notifications"] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(["notifications"]);

      // Optimistically update to remove this notification
      queryClient.setQueryData(["notifications"], (old: any) => {
        if (!old) return old;
        const notificationToDelete = old.notifications.find(
          (n: any) => n.id === notificationId
        );
        return {
          ...old,
          notifications: old.notifications.filter(
            (notification: any) => notification.id !== notificationId
          ),
          unreadCount:
            notificationToDelete && !notificationToDelete.read
              ? Math.max(0, old.unreadCount - 1)
              : old.unreadCount,
        };
      });

      // Return a context object with the snapshotted value
      return { previousData };
    },
    onError: (error, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousData) {
        queryClient.setQueryData(["notifications"], context.previousData);
      }
      // Error handling for delete notification
    },
    onSettled: () => {
      // Always refetch after error or success to ensure server state
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const clearAllMutation = useMutation({
    mutationFn: () => notificationService.clearAllNotifications(),
    onMutate: async () => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["notifications"] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(["notifications"]);

      // Optimistically update to clear all notifications
      queryClient.setQueryData(["notifications"], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          notifications: [],
          unreadCount: 0,
        };
      });

      // Return a context object with the snapshotted value
      return { previousData };
    },
    onError: (error, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousData) {
        queryClient.setQueryData(["notifications"], context.previousData);
      }
      // Error handling for clear all notifications
    },
    onSettled: () => {
      // Always refetch after error or success to ensure server state
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  // Show first 5 notifications by default, or all if showAll is true
  const displayedNotifications = showAll
    ? notifications
    : notifications.slice(0, 5);
  const hasMoreNotifications = notifications.length > 5;

  // Component state logging for debugging (can be removed in production)

  const getIcon = (type: string) => {
    switch (type) {
      case "new-rsvp":
        return <CheckCircleIcon className="w-4 h-4 text-green-600" />;
      case "rsvp-updated":
        return <InformationCircleIcon className="w-4 h-4 text-blue-600" />;
      case "rsvp-deleted":
        return <ExclamationTriangleIcon className="w-4 h-4 text-red-600" />;
      default:
        return <BellIcon className="w-4 h-4 text-gray-600" />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "new-rsvp":
        return "New RSVP";
      case "rsvp-updated":
        return "RSVP Updated";
      case "rsvp-deleted":
        return "RSVP Deleted";
      default:
        return "Notification";
    }
  };

  return (
    <div className="relative">
      {/* Notification Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
      >
        <BellIcon className="w-6 h-6" />
        {unreadCount > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium"
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </motion.span>
        )}
      </button>

      {/* Notification Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />

            {/* Dropdown Panel */}
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  Notifications
                </h3>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => navigate("/admin/notifications")}
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    title="Notification Settings"
                  >
                    <Cog6ToothIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Actions */}
              {notifications.length > 0 && (
                <div className="flex items-center justify-between p-3 bg-gray-50 border-b border-gray-200">
                  <div className="text-sm text-gray-600">
                    {unreadCount} unread notification
                    {unreadCount !== 1 ? "s" : ""}
                  </div>
                  <div className="flex items-center space-x-2">
                    {unreadCount > 0 && (
                      <button
                        onClick={() => markAllAsReadMutation.mutate()}
                        disabled={markAllAsReadMutation.isPending}
                        className={`text-xs font-medium disabled:opacity-50 transition-all duration-150 ${
                          markAllAsReadMutation.isPending
                            ? "text-blue-400 cursor-not-allowed"
                            : "text-blue-600 hover:text-blue-800 hover:scale-105 active:scale-95"
                        }`}
                      >
                        {markAllAsReadMutation.isPending ? (
                          <div className="flex items-center space-x-1">
                            <div className="w-3 h-3 border border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                            <span>Marking...</span>
                          </div>
                        ) : (
                          "Mark all read"
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => clearAllMutation.mutate()}
                      disabled={clearAllMutation.isPending}
                      className={`text-xs font-medium disabled:opacity-50 transition-all duration-150 ${
                        clearAllMutation.isPending
                          ? "text-red-400 cursor-not-allowed"
                          : "text-red-600 hover:text-red-800 hover:scale-105 active:scale-95"
                      }`}
                    >
                      {clearAllMutation.isPending ? (
                        <div className="flex items-center space-x-1">
                          <div className="w-3 h-3 border border-red-400 border-t-transparent rounded-full animate-spin"></div>
                          <span>Clearing...</span>
                        </div>
                      ) : (
                        "Clear all"
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Notifications List */}
              <div className="max-h-96 overflow-y-auto">
                {isLoading ? (
                  <div className="p-6 text-center text-gray-500">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <p className="text-sm">Loading notifications...</p>
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    <BellIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No notifications yet</p>
                  </div>
                ) : (
                  <>
                    <div className="divide-y divide-gray-200">
                      {displayedNotifications.map((notification) => (
                        <motion.div
                          key={notification.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          className={`p-4 hover:bg-gray-50 cursor-pointer transition-all duration-150 hover:scale-[1.02] ${
                            !notification.read ? "bg-blue-50" : ""
                          }`}
                          onClick={() => {
                            if (!notification.read) {
                              markAsReadMutation.mutate(notification.id);
                            }
                          }}
                        >
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0 mt-0.5">
                              {getIcon(notification.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <p className="text-sm font-medium text-gray-900">
                                  {getTypeLabel(notification.type)}
                                </p>
                                {!notification.read && (
                                  <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></div>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 mt-1">
                                {notification.message}
                              </p>
                              <p className="text-xs text-gray-500 mt-2">
                                {new Date(
                                  notification.timestamp
                                ).toLocaleString()}
                              </p>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteNotificationMutation.mutate(
                                  notification.id
                                );
                              }}
                              disabled={deleteNotificationMutation.isPending}
                              className="flex-shrink-0 text-gray-400 hover:text-red-600 transition-all duration-150 hover:scale-110 active:scale-95 disabled:opacity-50"
                            >
                              {deleteNotificationMutation.isPending ? (
                                <div className="w-4 h-4 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                              ) : (
                                <XMarkIcon className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </motion.div>
                      ))}
                    </div>

                    {/* Show More/Less Button */}
                    {hasMoreNotifications && (
                      <div className="p-3 border-t border-gray-200 bg-gray-50">
                        <button
                          onClick={() => setShowAll(!showAll)}
                          className="w-full flex items-center justify-center space-x-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
                        >
                          <span>
                            {showAll
                              ? "Show less"
                              : `Show ${
                                  notifications.length - 5
                                } more notifications`}
                          </span>
                          <ChevronDownIcon
                            className={`w-4 h-4 transition-transform ${
                              showAll ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationCenter;
